import { createRootRoute, createRoute, createRouter, redirect } from '@tanstack/react-router';

import { RootLayout } from './routes/__root';
import { LoginScreen } from './routes/login';
import { ForgotPinScreen } from './routes/forgot-pin';
// Public onboarding flow (sits beside /login on rootRoute — NOT behind the
// _app auth gate). Claim = take ownership of a coop-registered pin-less
// account; reuses the /auth OTP primitive. (G1 self-signup is out of pilot scope.)
import { ClaimScreen } from './routes/claim';
// Design-system gallery — public, unauthenticated north-star reference at
// /design. Not behind the _app auth gate and not linked from product nav.
import { DesignGalleryScreen } from './routes/design';
import { AppLayout } from './routes/_app';
import { DashboardScreen } from './routes/_app.dashboard';
import { FarmersScreen } from './routes/_app.farmers';
import { NewFarmerScreen } from './routes/_app.farmers.new';
import { FarmerDetailScreen } from './routes/_app.farmers.$userId';
import { PendingReviewsScreen } from './routes/_app.pending-reviews';
import { KycReviewsScreen } from './routes/_app.kyc-reviews';
import { BatchesScreen } from './routes/_app.batches';
import { BatchDetailScreen } from './routes/_app.batches.$id';
import { SettingsScreen } from './routes/_app.settings';
import { ProfileScreen } from './routes/_app.profile';
import { PrivacyScreen } from './routes/_app.privacy';
// Coop-member farmer self-service surface — the web mirror of
// the mobile farmer screens. Every route below sits on JwtAuthGuard-only or
// self-scoped endpoints (/me/*, /contributions?farmerUserId=self, /kyc/me), so
// a farmer can use them without tripping the admin-gated farmers/batches APIs.
import { HomeScreen } from './routes/_app.home';
import { MyFarmScreen } from './routes/_app.my-farm';
import { MyFarmEditScreen } from './routes/_app.my-farm.edit';
import { MyContributionsScreen } from './routes/_app.my-contributions';
import { MyActivityScreen } from './routes/_app.my-activity';
import { MyDataScreen } from './routes/_app.my-data';
import { ChangePinScreen } from './routes/_app.change-pin';
import { VerificationScreen } from './routes/_app.verification';
import { NotificationsScreen } from './routes/_app.notifications';
// Phase 3 — Traceability + Market Access. Public provenance viewer sits on
// rootRoute (no auth — a buyer scans a batch QR); market + demands are authed.
import { VerifyProvenanceScreen } from './routes/verify.$kind.$id';
import { MarketScreen } from './routes/_app.market';
import { DemandsScreen } from './routes/_app.demands';
// Marketplace transactional layer (Phase 5). Public buyer self-signup sits on
// rootRoute beside /login; the browse/offer/listing/order surfaces are authed.
import { BuyerSignupScreen } from './routes/buyer-signup';
import { MarketplaceScreen } from './routes/_app.marketplace';
import { MarketplaceListingScreen } from './routes/_app.marketplace.$id';
import { MyOffersScreen } from './routes/_app.my-offers';
import { BuyerVerificationScreen } from './routes/_app.buyer-verification';
import { MyListingsScreen } from './routes/_app.my-listings';
import { OrdersScreen } from './routes/_app.orders';
import { OrderDetailScreen } from './routes/_app.orders.$id';
// Client-only buyer Watchlist (Builder D — starred ids in localStorage, resolved
// against the marketplace browse cache). Reached from the marketplace header
// shortcut + the buyer-scoped sidebar item below; sits behind the _app auth gate.
import { WatchlistScreen } from './routes/_app.watchlist';
import { BuyerAccountScreen } from './routes/_app.buyer-account';
// Buyer demand form (#1a), public-ish market-price dashboard (#1b), and the
// farmer rewards/incentives wallet (#4). All sit behind the _app auth gate; the
// sidebar in _app.tsx scopes their links by role (buyer / broad / farmer).
import { CreateDemandScreen } from './routes/_app.create-demand';
import { PriceDashboardScreen } from './routes/_app.price-dashboard';
import { IncentivesScreen } from './routes/_app.incentives';
import { readCurrentRole, readAccessTokenClaims } from './api/client';

// Tree assembled in code (no router-plugin dependency). Each screen
// is a plain component; routes here own paths, layouts, and guards.
const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    // Route by role on landing. sysCollab → kanban. Any other authenticated
    // role → dashboard. No token → login.
    const claims = readAccessTokenClaims();
    if (!claims) {
      throw redirect({ to: '/login' });
    }
    if (claims.role === 'sysCollab') {
      // Hard nav out of the SPA — kanban is a separate static page.
      window.location.assign('/kanban.html');
      // Throw a redirect so TanStack stops processing; the hard nav
      // happens before this would matter, but be tidy.
      throw redirect({ to: '/login' });
    }
    // Farmers (coop members) land on their own self-service home.
    // The admin-oriented /dashboard, /farmers and /batches surfaces are
    // server-gated to admin/coop_admin and would be broken affordances for them.
    if (claims.role === 'farmer' || claims.role === 'group_lead') {
      throw redirect({ to: '/home' });
    }
    // Buyers (marketplace transactional layer) have no coop/farm surface — their
    // home is the marketplace browse. /dashboard is admin-gated and would be a
    // broken affordance for them.
    if (claims.role === 'buyer') {
      throw redirect({ to: '/marketplace' });
    }
    throw redirect({ to: '/dashboard' });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginScreen,
});

// Public coop_admin PIN-recovery flow (reuses the deployed /auth OTP + reset
// endpoints). Sits beside /login on rootRoute — NOT behind the _app auth gate.
const forgotPinRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-pin',
  component: ForgotPinScreen,
});

// Public account-claim for coop-registered (pin-less) farmers
// (phone → OTP → set PIN → POST /auth/claim). Mints tokens → /home.
const claimRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/claim',
  component: ClaimScreen,
});

// Public design-system gallery — sits beside /login on rootRoute (no auth).
const designRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/design',
  component: DesignGalleryScreen,
});

// Public provenance viewer (Phase 3) — a buyer scans a batch QR → this page →
// the full trace trail + cryptographic-verification badge. Sits beside /design
// on rootRoute with NO auth gate; reads the public GET /trace/subject/:kind/:id.
const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify/$kind/$id',
  component: VerifyProvenanceScreen,
});

const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_app',
  component: AppLayout,
  // Auth gate for the entire operational surface. sysCollab is bounced to
  // /kanban.html — they have no business in the coop_admin / farmer app.
  // No token at all → /login. Any other authenticated role passes.
  beforeLoad: () => {
    const role = readCurrentRole();
    if (!role) {
      throw redirect({ to: '/login' });
    }
    if (role === 'sysCollab') {
      window.location.assign('/kanban.html');
      // The hard nav already happened; this redirect just halts the router.
      throw redirect({ to: '/login' });
    }
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/dashboard',
  component: DashboardScreen,
});

const farmersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/farmers',
  component: FarmersScreen,
});

const newFarmerRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/farmers/new',
  component: NewFarmerScreen,
});

// Reached from a roster row (not the sidebar). `/farmers/new` is a static
// segment so TanStack ranks it above this `$userId` param route — no clash.
const farmerDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/farmers/$userId',
  component: FarmerDetailScreen,
});

const pendingReviewsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/pending-reviews',
  component: PendingReviewsScreen,
});

// Tier-1 KYC review queue (coop-vouched verification). Coop-admin/admin only;
// server scopes coop_admin to farmers in cooperatives they admin.
const kycReviewsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/kyc-reviews',
  component: KycReviewsScreen,
});

const batchesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/batches',
  component: BatchesScreen,
});

const batchDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/batches/$id',
  component: BatchDetailScreen,
});

const settingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings',
  component: SettingsScreen,
});

// Self-service profile editor (D-021 Path A). Any authenticated operational
// role — the /me/profile endpoint is JwtAuthGuard-only and scopes to the
// caller's own row.
const profileRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/profile',
  component: ProfileScreen,
});

// Read-only POPIA privacy notice (GET /consent/current). Visible to every
// authenticated role.
const privacyRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/privacy',
  component: PrivacyScreen,
});

// --------------------------------------------------------------------------
// Coop-member farmer self-service routes. All sit behind the
// same _app auth gate; the sidebar in _app.tsx scopes their links to the
// `farmer` role. The endpoints behind them are self-scoped, so the server is
// still the authority — these routes just give farmers a real surface instead
// of the admin pages they can't use.
// --------------------------------------------------------------------------

// Farmer landing — replaces /dashboard for the farmer role (see index redirect).
const homeRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/home',
  component: HomeScreen,
});

const myFarmRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/my-farm',
  component: MyFarmScreen,
});

// Static `/my-farm/edit` ranks above nothing here (no param sibling), but the
// nested path keeps the editor grouped under the read view.
const myFarmEditRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/my-farm/edit',
  component: MyFarmEditScreen,
});

const myContributionsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/my-contributions',
  component: MyContributionsScreen,
});

const myActivityRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/my-activity',
  component: MyActivityScreen,
});

const myDataRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/my-data',
  component: MyDataScreen,
});

const changePinRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/change-pin',
  component: ChangePinScreen,
});

const verificationRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/verification',
  component: VerificationScreen,
});

const notificationsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/notifications',
  component: NotificationsScreen,
});

// Phase 3 — Market Access (authed). Indicative price view (farmer/coop_admin)
// + the coop-admin incoming-demand list. Matching is surfaced on batch detail.
const marketRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/market',
  component: MarketScreen,
});

const demandsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/demands',
  component: DemandsScreen,
});

// --------------------------------------------------------------------------
// Marketplace transactional layer (Phase 5). Buyer self-signup is PUBLIC (sits
// on rootRoute beside /login); the rest sit behind the _app auth gate. Browse +
// my-offers + buyer-verification are buyer-facing; my-listings + orders are
// seller/party surfaces — the sidebar in _app.tsx scopes the links by role, the
// server is the authority (buyer-KYC gate on offers/demands).
// --------------------------------------------------------------------------

const buyerSignupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/buyer-signup',
  component: BuyerSignupScreen,
});

const marketplaceRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/marketplace',
  component: MarketplaceScreen,
});

// Static `/marketplace` ranks above this `$id` detail param route (same no-clash
// pattern as /farmers/new vs /farmers/$userId).
const marketplaceListingRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/marketplace/$id',
  component: MarketplaceListingScreen,
});

const myOffersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/my-offers',
  component: MyOffersScreen,
});

// Buyer Watchlist (client-only). Same _app auth gate; sidebar-scoped to `buyer`.
const watchlistRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/watchlist',
  component: WatchlistScreen,
});

const buyerVerificationRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/buyer-verification',
  component: BuyerVerificationScreen,
});

const myListingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/my-listings',
  component: MyListingsScreen,
});

const ordersRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/orders',
  component: OrdersScreen,
});

const orderDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/orders/$id',
  component: OrderDetailScreen,
});

// Buyer Account hub (#9) — buyer stat grid; sidebar-scoped to `buyer` in _app.tsx.
const buyerAccountRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/buyer-account',
  component: BuyerAccountScreen,
});

// Buyer demand form (#1a) — a verified buyer posts a "want to buy" demand. The
// nav link is buyer-scoped in _app.tsx; the screen gates on buyer KYC eligibility.
const createDemandRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/create-demand',
  component: CreateDemandScreen,
});

// Market-price dashboard (#1b) — indicative low/high/latest + a 30-day sparkline.
// Public-ish read (the underlying /market/prices endpoints are unauthenticated);
// the nav link is broad (all operational roles) in _app.tsx.
const priceDashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/price-dashboard',
  component: PriceDashboardScreen,
});

// Rewards / incentives wallet (#4) — balance + points ledger + earn-more tips.
// The nav link is farmer-scoped in _app.tsx; backed by the hand-rolled
// src/api/incentives.ts client (orval not yet regenerated for /incentives).
const incentivesRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/incentives',
  component: IncentivesScreen,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  forgotPinRoute,
  claimRoute,
  designRoute,
  verifyRoute,
  buyerSignupRoute,
  appLayoutRoute.addChildren([
    dashboardRoute,
    farmersRoute,
    newFarmerRoute,
    farmerDetailRoute,
    pendingReviewsRoute,
    kycReviewsRoute,
    batchesRoute,
    batchDetailRoute,
    settingsRoute,
    profileRoute,
    privacyRoute,
    // Coop-member farmer self-service surface (+ the role-aware /home)
    homeRoute,
    myFarmRoute,
    myFarmEditRoute,
    myContributionsRoute,
    myActivityRoute,
    myDataRoute,
    changePinRoute,
    verificationRoute,
    notificationsRoute,
    // Phase 3 — Market Access
    marketRoute,
    demandsRoute,
    // Phase 5 — Marketplace transactional layer
    marketplaceRoute,
    marketplaceListingRoute,
    myOffersRoute,
    watchlistRoute,
    buyerVerificationRoute,
    myListingsRoute,
    ordersRoute,
    orderDetailRoute,
    buyerAccountRoute,
    // Buyer demand form (#1a) / price dashboard (#1b) / incentives wallet (#4)
    createDemandRoute,
    priceDashboardRoute,
    incentivesRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
